import { useCallback } from "react";

import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import { External } from "components/icons/generated";
import Box from "ds/components/Box";
import DropdownMenuEllipsis, {
  DropdownMenuEllipsisProps,
} from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Icon from "ds/components/Icon";
import { TrackAnalyticsEventProperties } from "shared/Analytics";
import useCanManageCloudIntegration from "shared/CloudIntegration/access/useCanManageCloudIntegration";

import { AzureIntegrationView } from "../../useSearchCloudIntegrations/gql";
import { showAzureIntegrationDeleteConfirmation } from "../DeleteConfirmation";
import { showAzureIntegrationDetailsDrawer } from "../DetailsDrawer";
import { showAzureIntegrationFormDrawer } from "../FormDrawer";

type AzureIntegrationsActionsDropdownProps = {
  integration: AzureIntegrationView;
  trackAnalytics: (event: string, props?: TrackAnalyticsEventProperties) => void;
  hideSeeDetails?: boolean;
  onDeleteSuccess?: () => void;
  dotsSize?: DropdownMenuEllipsisProps["dotsSize"];
  refetchQueriesOnEdit: string[];
  refetchQueriesOnDelete?: string[];
};

// TODO: replace with AAC
const canDeleteAzureIntegration = (integration: AzureIntegrationView) => {
  return !integration.hasAttachedStacks;
};

const AzureIntegrationsActionsDropdown = ({
  integration,
  trackAnalytics,
  hideSeeDetails,
  onDeleteSuccess,
  dotsSize = "small",
  refetchQueriesOnEdit,
  refetchQueriesOnDelete,
}: AzureIntegrationsActionsDropdownProps) => {
  const canManageCloudIntegration = useCanManageCloudIntegration();

  const handleEditIntegration = useCallback(async () => {
    showAzureIntegrationFormDrawer({
      id: integration.id,
      integration,
      trackAnalytics,
      refetchQueriesOnEdit,
    });
  }, [integration, trackAnalytics, refetchQueriesOnEdit]);

  const handleDeleteIntegration = useCallback(() => {
    showAzureIntegrationDeleteConfirmation({
      integration,
      onDeleteSuccess,
      refetchQueriesOnDelete,
    });
  }, [integration, onDeleteSuccess, refetchQueriesOnDelete]);

  const handleSeeDetails = useCallback(() => {
    showAzureIntegrationDetailsDrawer({ id: integration.id, integration, trackAnalytics });
  }, [integration, trackAnalytics]);

  const handleReConsent = useCallback(() => {
    window.location.href = integration.adminConsentURL;
  }, [integration]);

  const hasAccessToManageIntegration = canManageCloudIntegration(integration);
  const canDeleteIntegration = canDeleteAzureIntegration(integration);

  return (
    <DropdownMenuEllipsis tooltip="Integration actions" dotsSize={dotsSize}>
      <CopyFieldDropdownMenuItem title="Copy ID" value={integration.id} />
      {!hideSeeDetails && (
        <DropdownMenuItem onAction={handleSeeDetails}>See details</DropdownMenuItem>
      )}
      <DropdownMenuItem
        onAction={handleEditIntegration}
        tooltip={
          !hasAccessToManageIntegration
            ? "You don't have access to manage this integration"
            : undefined
        }
        isTooltipActive={!hasAccessToManageIntegration}
        isDisabled={!hasAccessToManageIntegration}
        tooltipPlacement="left"
        tooltipWidthMode="maxWidthSm"
      >
        Edit
      </DropdownMenuItem>
      {integration.adminConsentProvided && (
        <DropdownMenuItem
          onAction={handleReConsent}
          isDisabled={!hasAccessToManageIntegration}
          tooltip={
            !hasAccessToManageIntegration
              ? "You don't have access to manage this integration"
              : "Use this to grant admin consent again if you uninstalled the application from your directory by accident. "
          }
          tooltipPlacement="left"
          tooltipWidthMode="maxWidthSm"
        >
          <Box justify="between" align="center" fullWidth>
            Re-consent
            <Icon src={External} />
          </Box>
        </DropdownMenuItem>
      )}
      <DropdownMenuItem
        onAction={handleDeleteIntegration}
        danger
        isDisabled={!hasAccessToManageIntegration || !canDeleteIntegration}
        tooltip={
          !hasAccessToManageIntegration
            ? "You don't have access to manage this integration"
            : "Cannot delete this integration because it is attached to a stack"
        }
        isTooltipActive={!hasAccessToManageIntegration || !canDeleteIntegration}
        tooltipPlacement="left"
        tooltipWidthMode="maxWidthSm"
      >
        Delete
      </DropdownMenuItem>
    </DropdownMenuEllipsis>
  );
};

export default AzureIntegrationsActionsDropdown;
